import { Component, OnDestroy, OnInit } from '@angular/core';
import { IAccount, IAccountDetail, IGmfParams } from '@app/shared/models/banking.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { AccountBasicService } from '@app/shared/services/banking/account-basic.service';
import { AccountContext } from '@app/shared/services/context/account.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { AuthService } from '@auth0/auth0-angular';
import { Subject, switchMap, tap, catchError, EMPTY, first, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-exoneration',
  templateUrl: './exoneration.component.html',
  styleUrls: ['./exoneration.component.sass'],
})
export class ExonerationComponent extends SubscriberComponent implements OnInit, OnDestroy {
  account!: IAccountDetail | IAccount;
  initialState!: IDynamicDialogConfig;
  private closeModal$ = new Subject<void>();

  constructor(
    private readonly modalSvc: ModalService,
    private readonly _accountBasicSvc: AccountBasicService,
    private readonly _accountCtx: AccountContext,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _authSvc: AuthService
  ) {
    super();
  }

  ngOnInit() {
    this.getmodalData();
    this.logAmplitudeEvent();
    this.setupModalCloseSubscription();

    const accountCode = this._accountCtx.getAccountCode() || '';
    const getAccounts = this._accountCtx.getAccounts() || [];
    this.account = getAccounts.find((acc: any) => acc.code === accountCode) || ({} as IAccountDetail);

    this.confirmActionSubscription(accountCode);
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this.modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this.modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private createPayload(accountCode: string, agentData: any): IGmfParams {
    return {
      rppUserId: this._customerCtxSvc.getRppUserId(),
      accountCode,
      isGmf: !this.accountInfo?.is_gmf,
      agent_email: agentData?.email || '',
    };
  }

  private confirmActionSubscription(accountCode: string) {
    return this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        first(vaule => vaule),
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => {
          const payload = this.createPayload(accountCode, agentData);

          return this._accountBasicSvc.setGmf(payload).pipe(
            catchError(error => {
              this.modalSvc.closeWithErrorMsg(this.initialState.modalComponentData.modalName);
              return EMPTY;
            })
          );
        }),
        tap(() => {
          this.modalSvc.closeWithPendingMsg(this.initialState.modalComponentData.modalName);
        })
      )
      .subscribe();
  }

  private logAmplitudeEvent() {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.EXONERATION,
    });
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => (this.initialState = data),
    });
  }

  get accountInfo(): any {
    return this._accountBasicSvc.accountInfo;
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.unsubscribe();
  }
}
